import {
  Box,
  HStack,
  Icon,
  Text,
  VStack,
  Spinner,
  IconButton,
  Flex,
} from "@chakra-ui/react";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { FaCheck, FaDownload, FaTimes } from "react-icons/fa";
import { LOADING_STATES } from "../common/constants/common";
import {
  isInit,
  isFinished,
  isUploadError,
  isUploadProcessing,
  isUploadSuccess,
} from "../common/helper/commonHelper";
import { UploadStatus } from "../common/types/person";
import DropZoneContentContainer from "./DropZoneContentContainer";
import IButton from "./IButton";
import IDropzone from "./IDropZone";
import IModal, { ModalButtonContent } from "./IModal";

function DownloadButton({
  onClick,
  isLoading,
}: {
  onClick: () => void;
  isLoading: boolean;
}) {
  return (
    <IButton
      capitalize={false}
      size="xs"
      variant="link"
      leftIcon={<FaDownload />}
      borderRadius="2px"
      name="download-csv-template"
      borderTopRightRadius="md"
      onClick={onClick}
      isLoading={isLoading}
    >
      Download CSV template
    </IButton>
  );
}

function FileUploadBar({
  fileName,
  status,
  removeFile,
}: {
  fileName: string;
  status: UploadStatus["status"] | undefined;
  removeFile: () => void;
}) {
  return (
    <HStack
      justifyContent="space-between"
      bg="gray.100"
      border="1px solid"
      borderColor="gray.200"
      px={3}
      py={1}
      w="60%"
      borderTopRadius="sm"
    >
      <HStack maxW="90%">
        {!status || isUploadProcessing(status) ? (
          <Spinner color="brand.blue" size="sm" />
        ) : (
          <Icon
            color={isUploadSuccess(status) ? "brand.green" : "brand.red"}
            as={FaCheck}
          />
        )}
        <Text color="brand.blue" fontSize="11px">
          {`Upload${!status || isUploadProcessing(status) ? "ing" : "ed"}`}
        </Text>
        <Box
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          maxWidth="65%"
        >
          {fileName}
        </Box>
      </HStack>
      <IconButton
        aria-label="Close"
        variant="solid"
        colorScheme="blackAlpha"
        bg="black"
        name="remove-file"
        size="xs"
        ml="2"
        color="white"
        icon={<FaTimes />}
        borderRadius="50%"
        onClick={removeFile}
      />
    </HStack>
  );
}

function FileUploadStatus({
  status,
  message,
  show,
}: {
  status: UploadStatus["status"] | undefined;
  message: string;
  show?: boolean;
}) {
  return (
    <Box
      height="20px"
      width="60%"
      borderBottomRadius="md"
      px={2}
      bg={isUploadSuccess(status) ? "brand.green" : "brand.red"}
      visibility={
        (isUploadSuccess(status) || isUploadError(status)) && show
          ? "visible"
          : "hidden"
      }
    >
      <Text fontSize="12px" color="white" fontWeight="500">
        {message}
      </Text>
    </Box>
  );
}

export default function UploadCSVModal({
  modalHeader,
  primaryButtonProps,
  secondaryButtonProps,
  isOpen,
  onClose,
  filepath,
  fetchingUploadStatus,
  fileUploadStatus,
  isDownloadingTemplate,
  downloadTemplateHandler,
  upload,
  checkUploadStatus,
  resetData,
  dropzoneContent,
}: {
  modalHeader: string;
  primaryButtonProps?: ModalButtonContent;
  secondaryButtonProps?: ModalButtonContent;
  isOpen: boolean;
  onClose: () => void;

  filepath: string;
  fetchingUploadStatus: LOADING_STATES;
  fileUploadStatus: UploadStatus | null;
  isDownloadingTemplate: boolean;

  downloadTemplateHandler: () => void;
  upload: (file: File[]) => void;
  checkUploadStatus: () => void;
  resetData: () => void;

  dropzoneContent?: ReactNode;
}) {
  const [file, setFile] = useState<File[]>([]);
  const isSchedulerRunning = useRef<boolean>(false);

  useEffect(() => {
    setFile([]);
  }, [isOpen]);

  function onSelectFile(file: File[]) {
    if (file.length) {
      setFile(file);
      upload(file);
    }
  }

  const onModalClose = useCallback(() => {
    setFile([]);
    onClose();
    resetData();
  }, [resetData, onClose]);

  const callSporadically = useCallback(
    (
      func: () => void,
      interval: number,
      isSchedulerRunning: React.MutableRefObject<boolean>
    ) => {
      if (isSchedulerRunning.current) {
        setTimeout(function () {
          func.call(undefined);
          // limit the gap between two status calls to not go beyond 1 minute
          if (interval < 30000) {
            interval = interval * 2;
          }
          callSporadically(func, interval, isSchedulerRunning);
        }, interval);
      }
    },
    []
  );

  useEffect(() => {
    if (
      file.length &&
      filepath &&
      isInit(fetchingUploadStatus) &&
      !isSchedulerRunning.current
    ) {
      isSchedulerRunning.current = true;
      callSporadically(checkUploadStatus, 2000, isSchedulerRunning);
    } else if (
      (!filepath || isFinished(fetchingUploadStatus)) &&
      isSchedulerRunning.current
    ) {
      isSchedulerRunning.current = false;
    }
  }, [
    filepath,
    fileUploadStatus,
    fetchingUploadStatus,
    file.length,
    checkUploadStatus,
    callSporadically,
  ]);

  useEffect(() => {
    return () => {
      isSchedulerRunning.current = false;
    };
  }, []);

  return (
    <IModal
      header={{ title: modalHeader, capitalize: false }}
      primaryButton={primaryButtonProps}
      secondaryButton={{
        ...{ label: "Cancel", props: { onClick: onModalClose } },
        ...secondaryButtonProps,
      }}
      isOpen={isOpen}
      onClose={onModalClose}
      size="2xl"
    >
      <Flex w="100%" justifyContent="flex-end" mb="2">
        <DownloadButton
          onClick={downloadTemplateHandler}
          isLoading={isDownloadingTemplate}
        />
      </Flex>
      <VStack py={0} overflow="hidden" position="relative">
        <IDropzone onSelectFile={onSelectFile} disabled={!!file.length}>
          <DropZoneContentContainer>{dropzoneContent}</DropZoneContentContainer>
        </IDropzone>
        <VStack position="absolute" bottom="0px" width="100%" spacing={0}>
          {file.map((item, index) => (
            <FileUploadBar
              fileName={item.name}
              key={index}
              status={fileUploadStatus?.status}
              removeFile={() => {
                setFile([]);
                resetData();
              }}
            />
          ))}
          <FileUploadStatus
            status={fileUploadStatus?.status}
            message={fileUploadStatus?.error || "Uploaded succesfully"}
            show={!!file.length}
          />
        </VStack>
      </VStack>
    </IModal>
  );
}
